import React, { useEffect } from 'react';
import AOS from 'aos';
import 'aos/dist/aos.css'; 

// Images
import Bannerimage4 from "./img/Bannerimage4.jpg";
import Bannerimage3 from "./img/Bannerimage3.jpg";
import rooms from "./img/rooms.jpg";
import Counseling from "./img/Counseling.webp";
import hands from "./img/hands.webp";
import Dining from "./img/Dining.jpg";
import Familly from "./img/Familly.jpg";
import Recovery from "./img/Recovery.webp";

const Facility = () => {
    useEffect(() => {
        // Enhanced AOS settings for smoother luxury animations
        AOS.init({ duration: 1000, easing: 'ease-out-cubic', once: true, offset: 50 });
    }, []);

    return (
        <div id="ultra-premium-facility">
            <style>
                {`
                    #ultra-premium-facility {
                        /* Brand Colors & Luxury Theme */
                        --brand-pink: #ec1c74;
                        --brand-blue: #00a8e8;
                        --bg-light: #fbfcfd;
                        --text-dark: #1d1d1f;
                        --text-muted: #86868b;
                        --glass-bg: rgba(255, 255, 255, 0.7);
                        --glass-border: rgba(255, 255, 255, 0.9);
                        --accent-gradient: linear-gradient(135deg, var(--brand-blue), var(--brand-pink));
                        
                        font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
                        background-color: var(--bg-light);
                        padding: 6rem 2rem;
                        overflow: hidden;
                        position: relative;
                    }

                    /* Ambient Glowing Orbs */
                    #ultra-premium-facility::before,
                    #ultra-premium-facility::after {
                        content: '';
                        position: absolute;
                        border-radius: 50%;
                        filter: blur(140px);
                        z-index: 0;
                        opacity: 0.3;
                        animation: floatBg 12s ease-in-out infinite alternate;
                    }

                    #ultra-premium-facility::before {
                        width: 600px; height: 600px;
                        background: rgba(0, 168, 232, 0.15);
                        top: 10%; left: -10%;
                    }

                    #ultra-premium-facility::after {
                        width: 500px; height: 500px;
                        background: rgba(236, 28, 116, 0.15);
                        bottom: 20%; right: -10%;
                        animation-delay: -6s;
                    }

                    @keyframes floatBg {
                        0% { transform: translate(0, 0); }
                        100% { transform: translate(40px, 60px); }
                    }

                    #ultra-premium-facility * {
                        box-sizing: border-box;
                        position: relative;
                        z-index: 1;
                    }

                    .facility-container {
                        max-width: 1200px;
                        margin: 0 auto;
                    }

                    /* Hero Header */
                    .facility-header {
                        text-align: center;
                        margin-bottom: 6rem;
                    }

                    .facility-heading {
                        font-size: 3.8rem;
                        font-weight: 800;
                        color: var(--text-dark);
                        letter-spacing: -0.04em;
                        margin-bottom: 1rem;
                    }

                    .facility-heading span {
                        background: var(--accent-gradient);
                        -webkit-background-clip: text;
                        -webkit-text-fill-color: transparent;
                    }

                    .facility-subheading {
                        font-size: 1.25rem;
                        color: var(--text-muted);
                        max-width: 700px;
                        margin: 0 auto;
                        line-height: 1.6;
                    }

                    /* Zig-Zag Editorial Layout */
                    .facility-card {
                        display: flex;
                        align-items: center;
                        gap: 4rem;
                        margin-bottom: 6rem;
                        background: var(--glass-bg);
                        backdrop-filter: blur(20px);
                        -webkit-backdrop-filter: blur(20px);
                        border: 1px solid var(--glass-border);
                        border-radius: 40px;
                        padding: 2.5rem;
                        box-shadow: 0 20px 50px rgba(0, 0, 0, 0.03);
                        transition: transform 0.5s ease, box-shadow 0.5s ease;
                    }

                    /* Alternate image position on desktop */
                    .facility-card:nth-child(even) {
                        flex-direction: row-reverse;
                    }

                    .facility-card:hover {
                        transform: translateY(-8px);
                        box-shadow: 0 30px 60px rgba(0, 168, 232, 0.08);
                    }

                    /* Image Wrapper */
                    .facility-image-wrapper {
                        flex: 1.2;
                        border-radius: 28px;
                        overflow: hidden;
                        box-shadow: 0 15px 35px rgba(0, 0, 0, 0.1);
                        position: relative;
                    }

                    .facility-image-wrapper::after {
                        content: '';
                        position: absolute;
                        inset: 0;
                        border-radius: 28px;
                        border: 1px solid rgba(255, 255, 255, 0.2);
                        pointer-events: none;
                    }

                    .facility-image {
                        width: 100%;
                        height: 100%;
                        min-height: 350px;
                        object-fit: cover;
                        display: block;
                        transition: transform 0.8s cubic-bezier(0.165, 0.84, 0.44, 1);
                    }

                    .facility-card:hover .facility-image {
                        transform: scale(1.05);
                    }

                    /* Text Content */
                    .facility-content {
                        flex: 1;
                        padding: 1rem;
                    }

                    .facility-badge {
                        font-size: 0.85rem;
                        font-weight: 700;
                        text-transform: uppercase;
                        letter-spacing: 0.15em;
                        color: var(--brand-pink);
                        margin-bottom: 1rem;
                        display: inline-block;
                    }
                    
                    .facility-card:nth-child(even) .facility-badge {
                        color: var(--brand-blue);
                    }

                    .facility-content h2 {
                        font-size: 2.2rem;
                        font-weight: 700;
                        color: var(--text-dark);
                        margin: 0 0 1.2rem 0;
                        letter-spacing: -0.02em;
                        line-height: 1.2;
                    }

                    .facility-content p {
                        font-size: 1.1rem;
                        color: var(--text-muted);
                        line-height: 1.7;
                        margin: 0;
                    }

                    /* Responsive Tweaks */
                    @media (max-width: 1024px) {
                        .facility-card { gap: 2rem; padding: 2rem; }
                        .facility-heading { font-size: 3.2rem; }
                        .facility-content h2 { font-size: 1.8rem; }
                        .facility-image { min-height: 300px; }
                    }

                    @media (max-width: 768px) {
                        #ultra-premium-facility { padding: 4rem 1rem; }
                        .facility-heading { font-size: 2.5rem; }
                        
                        /* Stack vertically on mobile */
                        .facility-card, 
                        .facility-card:nth-child(even) {
                            flex-direction: column;
                            padding: 1.5rem;
                            gap: 1.5rem;
                            margin-bottom: 4rem;
                        }
                        
                        .facility-image-wrapper { width: 100%; }
                        .facility-image { min-height: 250px; }
                        .facility-content { padding: 0; text-align: center; }
                    }
                `}
            </style>

            <div className="facility-container">
                
                <div className="facility-header" data-aos="fade-up">
                    <h1 className="facility-heading">Our World-Class <br /><span>Wellness Facility</span></h1>
                    <p className="facility-subheading">
                        Experience healing and tranquility in an ultra-premium environment, meticulously designed to provide the ultimate care, comfort, and holistic rejuvenation.
                    </p>
                </div>

                {/* Facility 1 */}
                <section className="facility-card" data-aos="fade-up">
                    <div className="facility-content">
                        <span className="facility-badge">Premium Care</span>
                        <h2>Wellness & Care Facilities</h2>
                        <p>
                            Our wellness options are comprehensive and personalized to cater to the specific needs of each individual. We offer advanced holistic cleansing programs, health-assisted therapies, psychological counseling, and group therapy in a comfortable and supportive environment.
                        </p>
                    </div>
                    <div className="facility-image-wrapper">
                        <img src={Bannerimage4} alt="Treatment Room" className="facility-image" loading="lazy" />
                    </div>
                </section>

                {/* Facility 2 */}
                <section className="facility-card" data-aos="fade-up">
                    <div className="facility-content">
                        <span className="facility-badge">Rejuvenation</span>
                        <h2>Recreational & Therapeutic</h2>
                        <p>
                            Our center promotes healing through relaxation and rejuvenation. We provide beautiful yoga and meditation rooms, nature therapy zones, art and music therapy, and a deeply peaceful environment to help individuals heal holistically.
                        </p>
                    </div>
                    <div className="facility-image-wrapper">
                        <img src={Bannerimage3} alt="Therapy Room" className="facility-image" loading="lazy" />
                    </div>
                </section>

                {/* Facility 3 */}
                <section className="facility-card" data-aos="fade-up">
                    <div className="facility-content">
                        <span className="facility-badge">Advanced Support</span>
                        <h2>Medical & Cleansing Facilities</h2>
                        <p>
                            Equipped with the most advanced medical technologies to ensure constant monitoring and flawless care. We offer 24/7 elite medical support, emergency care, dedicated holistic cleansing rooms, and carefully personalized health plans.
                        </p>
                    </div>
                    <div className="facility-image-wrapper">
                        <img src={rooms} alt="Medical Room" className="facility-image" loading="lazy" />
                    </div>
                </section>

                {/* Facility 4 */}
                <section className="facility-card" data-aos="fade-up">
                    <div className="facility-content">
                        <span className="facility-badge">Mental Wellness</span>
                        <h2>Counseling & Therapy</h2>
                        <p>
                            Our licensed expert counselors provide deeply empathetic one-on-one therapy, group therapy, and family counseling. Specialized therapies are designed to gracefully help you heal from past challenges and guide you towards lasting emotional balance.
                        </p>
                    </div>
                    <div className="facility-image-wrapper">
                        <img src={Counseling} alt="Counseling Room" className="facility-image" loading="lazy" />
                    </div>
                </section>

                {/* Facility 5 */}
                <section className="facility-card" data-aos="fade-up">
                    <div className="facility-content">
                        <span className="facility-badge">Pure Environment</span>
                        <h2>Hygiene & Safety</h2>
                        <p>
                            Our luxurious facility strictly adheres to the highest global standards of hygiene and cleanliness. We utilize state-of-the-art sanitation procedures to ensure that all areas, from private rooms to therapy spaces, are impeccably safe and pristine.
                        </p>
                    </div>
                    <div className="facility-image-wrapper">
                        <img src={hands} alt="Hygiene and Safety" className="facility-image" loading="lazy" />
                    </div>
                </section>

                {/* Facility 6 */}
                <section className="facility-card" data-aos="fade-up">
                    <div className="facility-content">
                        <span className="facility-badge">Gourmet Healing</span>
                        <h2>Dining & Nutrition</h2>
                        <p>
                            Our elite nutrition experts ensure that each meal is flawlessly balanced, healthy, and perfectly tailored to your specific recovery needs. We offer delicious, farm-fresh meals designed to accelerate holistic cleansing and overall well-being.
                        </p>
                    </div>
                    <div className="facility-image-wrapper">
                        <img src={Dining} alt="Dining Area" className="facility-image" loading="lazy" />
                    </div>
                </section>

                {/* Facility 7 */}
                <section className="facility-card" data-aos="fade-up">
                    <div className="facility-content">
                        <span className="facility-badge">Lifelong Balance</span>
                        <h2>Continuous Support</h2>
                        <p>
                            Our commitment extends far beyond your stay. The continuous support program is designed to provide ongoing guidance through exclusive follow-up counseling, elite group therapy, and an inspiring wellness alumni network.
                        </p>
                    </div>
                    <div className="facility-image-wrapper">
                        <img src={Recovery} alt="Aftercare" className="facility-image" loading="lazy" />
                    </div>
                </section>

                {/* Facility 8 */}
                <section className="facility-card" data-aos="fade-up">
                    <div className="facility-content">
                        <span className="facility-badge">Community Care</span>
                        <h2>Family & Visitor Facilities</h2>
                        <p>
                            We deeply believe in the power of family support. Our premium visitor areas are beautifully designed for family members to visit comfortably, thoughtfully complemented by professional family counseling sessions.
                        </p>
                    </div>
                    <div className="facility-image-wrapper">
                        <img src={Familly} alt="Family and Visitor Area" className="facility-image" loading="lazy" />
                    </div>
                </section>

            </div>
        </div>
    );
};

export default Facility;